import { useState, useEffect, useRef, useCallback } from 'react'
import { trainingBlockApi } from '../../api'

interface ExerciseNameInputProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  textColor: string | undefined
  bgColor: string | undefined
  /** When true, wraps long names instead of truncating on one line. */
  multiline?: boolean
}

export function ExerciseNameInput({
  value,
  onChange,
  placeholder,
  textColor,
  bgColor,
  multiline = false,
}: ExerciseNameInputProps) {
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const fetchSuggestions = useCallback((q: string) => {
    if (!q.trim()) {
      setSuggestions([])
      setOpen(false)
      return
    }
    trainingBlockApi.getExerciseSuggestions(q).then(results => {
      setSuggestions(results)
      setOpen(results.length > 0)
    }).catch(() => { })
  }, [])

  function scheduleSuggest(val: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250)
  }

  function handleSelect(name: string) {
    onChange(name)
    setSuggestions([])
    setOpen(false)
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!multiline || !textareaRef.current) return
    const el = textareaRef.current
    el.style.height = 'auto'
    el.style.height = `${Math.max(44, el.scrollHeight)}px`
  }, [value, multiline])

  const fieldClass =
    'w-full rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-500/50 text-sm leading-snug'
  const fieldStyle = {
    color: textColor || '#000000',
    backgroundColor: bgColor || 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(156,163,175,0.25)',
  } as const

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      {multiline ? (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => {
            onChange(e.target.value)
            scheduleSuggest(e.target.value)
          }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          rows={2}
          className={`${fieldClass} resize-y min-h-[44px] whitespace-pre-wrap break-words`}
          style={{
            ...fieldStyle,
            overflow: 'auto',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
          autoComplete="off"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => {
            onChange(e.target.value)
            scheduleSuggest(e.target.value)
          }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className={fieldClass}
          style={fieldStyle}
          autoComplete="off"
        />
      )}
      {open && (
        <ul
          className="absolute z-10 top-full mt-1 left-0 right-0 rounded-xl overflow-hidden shadow-lg border border-gray-400/20 max-h-48 overflow-y-auto"
          style={{ backgroundColor: bgColor || '#ffffff' }}
        >
          {suggestions.map(s => (
            <li key={s}>
              <button
                type="button"
                onMouseDown={() => handleSelect(s)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors whitespace-normal break-words"
                style={{ color: textColor || '#000000' }}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
